import { useEffect, useState } from 'react';
import { api, type AdminOverview, type ClientSession } from '../api';
import { Loading } from '../components/Status';
import './admin-page.css';

export function CalendarMembersPage({
  session,
  calendarId,
}: {
  session: ClientSession | null;
  calendarId: string;
}) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');

  const load = async () => {
    setError('');
    try {
      setOverview(await api.admin());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Calendar access could not load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => void load(), [calendarId]);

  const run = async (name: string, action: () => Promise<void>) => {
    setBusy(name);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The change could not be saved');
    } finally {
      setBusy('');
    }
  };

  if (loading)
    return (
      <main id="main-content" className="center-page">
        <Loading label="Loading calendar access" />
      </main>
    );
  if (!session?.user)
    return (
      <main id="main-content" className="center-page">
        <h1>Sign in to manage calendar access</h1>
        <a
          className="button"
          href={`/login?callbackURL=${encodeURIComponent(window.location.pathname)}`}
        >
          Continue to sign in
        </a>
      </main>
    );

  const calendar = overview?.calendars.find((candidate) => candidate.id === calendarId);
  if (!overview || !calendar)
    return (
      <main id="main-content" className="center-page">
        <h1>Calendar access unavailable</h1>
        <p className="inline-error" role="alert">
          {error || 'This calendar is unavailable or you do not have permission to manage it.'}
        </p>
        <a className="button button-quiet" href="/admin">
          Back to administration
        </a>
      </main>
    );

  const memberships = overview.memberships.filter((member) => member.calendar_id === calendarId);
  const invitations = overview.invitations.filter(
    (invitation) => invitation.calendar_id === calendarId,
  );
  const canInvite = !calendar.familyOnly || overview.parentAccessEnabled;
  const isAdmin = session.user.role === 'admin' || !!session.capabilities.superAdmin;

  return (
    <main id="main-content" className="settings-page admin-page">
      <a className="back-link" href="/admin">
        ← Administration
      </a>
      <h1>{calendar.name} access</h1>
      <p>Manage members and invitations for this calendar.</p>
      {calendar.staffOnly && (
        <section className="settings-card">
          <h2>Inherited staff access</h2>
          <p>
            {calendar.staffAccess === 'all'
              ? `All eligible staff can ${calendar.staffDefaultRole === 'editor' ? 'edit' : 'view'} this calendar automatically. Individual roles can add permissions but cannot reduce that default. Removing a membership does not remove inherited staff access.`
              : 'Only selected staff members have access. Their membership role determines their permissions.'}
          </p>
          {isAdmin && <a href="/settings">Edit calendar access settings</a>}
        </section>
      )}
      {error && (
        <p className="inline-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="inline-success" role="status">
          {notice}
        </p>
      )}

      <section className="settings-card">
        <h2>Members</h2>
        <p>
          Add someone who has already signed in. New members receive an explicit viewer role;
          inherited staff permissions still apply.
        </p>
        <div className="form-grid">
          <label className="field">
            <span>Member email address</span>
            <input
              type="email"
              value={memberEmail}
              onChange={(event) => setMemberEmail(event.target.value)}
            />
          </label>
          <button
            className="button"
            type="button"
            disabled={Boolean(busy) || !memberEmail}
            onClick={() =>
              void run('add-member', async () => {
                await api.addMembership(calendarId, memberEmail, session.csrfToken);
                setMemberEmail('');
                setNotice('Viewer membership added. Inherited staff permissions still apply.');
                await load();
              })
            }
          >
            Add member
          </button>
        </div>
        <div className="admin-list">
          {memberships.map((member) => {
            const selfManaged = !isAdmin && member.user_id === session.user?.id;
            return (
              <div
                className="admin-row"
                key={member.user_id}
                data-testid={`membership-${calendarId}-${member.user_id}`}
              >
                <span>
                  <b>{member.name || member.email || member.user_id}</b>
                  {member.email && <small>{member.email}</small>}
                </span>
                <label className="field admin-field">
                  <span className="sr-only">Calendar role</span>
                  <select
                    aria-label="Calendar role"
                    value={member.role}
                    disabled={Boolean(busy) || selfManaged}
                    onChange={(event) =>
                      void run(`member-${member.user_id}`, async () => {
                        await api.updateMembership(
                          calendarId,
                          member.user_id,
                          event.target.value as 'viewer' | 'editor' | 'manager',
                          session.csrfToken,
                        );
                        setNotice('Membership updated.');
                        await load();
                      })
                    }
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="manager">Manager</option>
                  </select>
                </label>
                <button
                  className="link-button"
                  type="button"
                  disabled={Boolean(busy) || selfManaged}
                  onClick={() =>
                    void run(`remove-${member.user_id}`, async () => {
                      await api.removeMembership(calendarId, member.user_id, session.csrfToken);
                      setNotice('Membership removed.');
                      await load();
                    })
                  }
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
        {!memberships.length && <p className="admin-empty">No members yet.</p>}
      </section>

      <section className="settings-card">
        <h2>Invitations</h2>
        {canInvite ? (
          <div className="form-grid">
            <label className="field">
              <span>Email address</span>
              <input
                type="email"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
              />
            </label>
            <button
              className="button"
              type="button"
              disabled={Boolean(busy) || !inviteEmail}
              onClick={() =>
                void run('invite', async () => {
                  const created = await api.adminInvitation(
                    inviteEmail,
                    calendarId,
                    session.csrfToken,
                  );
                  setInviteUrl(created.url);
                  setInviteEmail('');
                  setNotice('Invitation created.');
                  await load();
                })
              }
            >
              Create invitation
            </button>
          </div>
        ) : (
          <p>Invited-family access is currently disabled for this calendar.</p>
        )}
        {inviteUrl && (
          <p className="admin-invite-url">
            <b>Share this private link:</b> {inviteUrl}
          </p>
        )}
        <div className="admin-list">
          {invitations.map((invitation) => (
            <div className="admin-row" key={invitation.id}>
              <span>
                <b>{invitation.email}</b>
                <small>{invitation.created_by_name || 'Calendar manager'}</small>
              </span>
              <span className="admin-state">
                {invitation.revoked_at
                  ? 'Revoked'
                  : invitation.accepted_at
                    ? 'Accepted'
                    : 'Pending'}
              </span>
              {!invitation.revoked_at && !invitation.accepted_at && (
                <button
                  className="link-button"
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() =>
                    void run(`invite-${invitation.id}`, async () => {
                      await api.revokeAdminInvitation(invitation.id, session.csrfToken);
                      setNotice('Invitation revoked.');
                      await load();
                    })
                  }
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
        {!invitations.length && <p className="admin-empty">No invitations yet.</p>}
      </section>
    </main>
  );
}
